import BigNumber from 'bignumber.js';
import {Branded} from 'io-ts';
import {DoubleString} from '~/library/types';
import {CurrencyCode} from '../Currency/CurrencyCode';
import {FiatCurrencyCode} from '../Currency/FiatCurrencyCode';
import {NonNegativeDoubleString} from '../NonNegativeDoubleString';
import {NonNegativeInt} from '../NonNegativeInt';
import {PositiveInt} from '../PositiveInt';

interface CurrencyAmount {
    readonly currencyCode: CurrencyCode;
    readonly decimals: NonNegativeInt;
    readonly amount: NonNegativeDoubleString;
}

interface MoneyBrand {
    readonly Money: unique symbol;
}

export type Money = Branded<CurrencyAmount, MoneyBrand>;

export const isMoney = (input: CurrencyAmount): input is Money => {
    const [, fractional = ''] = input.amount.split('.'); // FIXME typesafe splitting into to substrings
    return fractional.length === input.decimals;
};

export const createMoney = (
    currencyCode: CurrencyCode,
    decimals: NonNegativeInt,
    value: NonNegativeDoubleString = '0' as NonNegativeDoubleString
): Money => {
    const amount = new BigNumber(value).toFixed(decimals, BigNumber.ROUND_DOWN) as NonNegativeDoubleString;
    return {currencyCode, decimals, amount} as Money;
};

interface PartialAccount {
    readonly balance: DoubleString;
    readonly decimals: NonNegativeInt;
    readonly currency: FiatCurrencyCode;
}

export const fromAccountBalance = ({balance, decimals, currency: currencyCode}: PartialAccount): Money => {
    const amount = new BigNumber(balance).toFixed(decimals, BigNumber.ROUND_DOWN) as DoubleString;
    return {currencyCode, decimals, amount} as Money;
};

export type MoneyPair = readonly [Money, Money];

interface SameCurrencyBrand {
    readonly SameCurrency: unique symbol;
}

export type SameCurrencyMoneyPair = Branded<MoneyPair, SameCurrencyBrand>;

export const isSameCurrencyMoneyPair = (pair: MoneyPair): pair is SameCurrencyMoneyPair =>
    pair[0].currencyCode === pair[1].currencyCode && pair[0].decimals === pair[1].decimals;

// FIXME find a better way of ensuring that money pair shares same currency
export const assertSameCurrencyMoneyPair: (pair: MoneyPair) => asserts pair is SameCurrencyMoneyPair = pair => {
    if (!isSameCurrencyMoneyPair(pair)) {
        throw new Error('Not SameCurrencyMoneyPair'); // FIXME actual error
    }
};

interface GteBrand {
    readonly Gte: unique symbol;
}

export type GteMoneyPair = Branded<SameCurrencyMoneyPair, GteBrand>;

export const gte = (pair: SameCurrencyMoneyPair): pair is GteMoneyPair => {
    return new BigNumber(pair[0].amount).gte(new BigNumber(pair[1].amount));
};

interface GtBrand {
    readonly Gt: unique symbol;
}

export type GtMoneyPair = Branded<GteMoneyPair, GtBrand>;

export const gt = (pair: SameCurrencyMoneyPair): pair is GtMoneyPair => {
    return new BigNumber(pair[0].amount).gt(new BigNumber(pair[1].amount));
};

export const isPositive = (money: Money) => {
    return new BigNumber(money.amount).isPositive();
};

export const isZero = (money: Money) => {
    return new BigNumber(money.amount).isZero();
};

interface LteBrand {
    readonly Lte: unique symbol;
}

export type LteMoneyPair = Branded<SameCurrencyMoneyPair, LteBrand>;

export const lte = (pair: SameCurrencyMoneyPair): pair is LteMoneyPair => {
    return new BigNumber(pair[0].amount).lte(new BigNumber(pair[1].amount));
};

interface LtBrand {
    readonly Lt: unique symbol;
}

export type LtMoneyPair = Branded<LteMoneyPair, LtBrand>;

export const lt = (pair: SameCurrencyMoneyPair): pair is LtMoneyPair => {
    return new BigNumber(pair[0].amount).lt(new BigNumber(pair[1].amount));
};

interface EqBrand {
    readonly Eq: unique symbol;
}

export type EqMoneyPair = Branded<Branded<GteMoneyPair, LteBrand>, EqBrand>;

export const eq = (pair: SameCurrencyMoneyPair): pair is EqMoneyPair => {
    return new BigNumber(pair[0].amount).eq(new BigNumber(pair[1].amount));
};

export const min = (pair: SameCurrencyMoneyPair): Money => {
    return lte(pair) ? pair[0] : pair[1];
};

export const max = (pair: SameCurrencyMoneyPair): Money => {
    return gte(pair) ? pair[0] : pair[1];
};

export const add = ([
    {currencyCode, decimals, amount: firstAmount},
    {amount: secondAmount},
]: SameCurrencyMoneyPair): Money => {
    const amount = new BigNumber(firstAmount)
        .plus(new BigNumber(secondAmount))
        .toFixed(decimals, BigNumber.ROUND_UP) as NonNegativeDoubleString;
    return {currencyCode, decimals, amount: amount} as Money;
};

export const subtract = ([
    {currencyCode, decimals, amount: firstAmount},
    {amount: secondAmount},
]: GteMoneyPair): Money => {
    const amount = new BigNumber(firstAmount)
        .minus(new BigNumber(secondAmount))
        .toFixed(decimals, BigNumber.ROUND_UP) as NonNegativeDoubleString;
    return {currencyCode, decimals, amount} as Money;
};

export const multiply = ({currencyCode, decimals, amount}: Money, factor: NonNegativeDoubleString): Money => {
    const [, factorFractional = ''] = factor.split('.'); // FIXME typesafe splitting into to substrings
    const result = new BigNumber(amount)
        .multipliedBy(new BigNumber(factor))
        .toFixed(decimals + factorFractional.length, BigNumber.ROUND_UP) as NonNegativeDoubleString;
    const newAmount = new BigNumber(result).toFixed(decimals, BigNumber.ROUND_UP);
    return {currencyCode, decimals, amount: newAmount} as Money;
};

export const exchange = (
    {decimals, amount}: Money,
    targetCurrencyCode: CurrencyCode,
    targetCurrencyDecimals: NonNegativeInt,
    rate: NonNegativeDoubleString
): Money => {
    const [, rateFractional = ''] = rate.split('.'); // FIXME typesafe splitting into to substrings
    const result = new BigNumber(amount)
        .multipliedBy(new BigNumber(rate))
        .toFixed(decimals + rateFractional.length, BigNumber.ROUND_UP) as NonNegativeDoubleString;
    const newAmount = new BigNumber(result).toFixed(targetCurrencyDecimals, BigNumber.ROUND_DOWN);
    return {currencyCode: targetCurrencyCode, decimals: targetCurrencyDecimals, amount: newAmount} as Money;
};

export const divide = ({currencyCode, decimals, amount}: Money, divider: PositiveInt): Money => {
    const result = new BigNumber(amount)
        .dividedBy(new BigNumber(divider))
        .toFixed(decimals, BigNumber.ROUND_UP) as NonNegativeDoubleString;
    const newAmount = new BigNumber(result).toFixed(decimals, BigNumber.ROUND_UP);
    return {currencyCode, decimals, amount: newAmount} as Money;
};
